import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  Timeline,
  Tag,
  Spin,
  Alert,
  Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  DesktopOutlined,
  FileTextOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useAuth } from '../hooks/useAuth';
import { getVisitorDetail } from '../services/api';
import type { VisitorDetailEvent } from '../services/api';
import LabelEditor from '../components/admin/LabelEditor';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === 0) return '-';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function formatPageName(event: VisitorDetailEvent): string {
  if (event.essaySlug) {
    return event.essaySlug
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  if (event.path === '/') return 'Enter Page';
  if (event.path === '/home') return 'Home';
  return event.path;
}

function VisitorDetailPage() {
  const { visitorHash } = useParams<{ visitorHash: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['visitorDetail', visitorHash],
    queryFn: () => getVisitorDetail(visitorHash!),
    enabled: !!visitorHash,
    refetchInterval: 60000,
  });

  // Group events by date
  const groupedEvents = useMemo(() => {
    if (!data?.events) return [];
    const groups = new Map<string, VisitorDetailEvent[]>();
    for (const event of data.events) {
      const dateKey = dayjs(event.viewedAt).format('YYYY-MM-DD');
      if (!groups.has(dateKey)) groups.set(dateKey, []);
      groups.get(dateKey)!.push(event);
    }
    return Array.from(groups.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, events]) => ({
        date,
        dateLabel: dayjs(date).format('dddd, MMMM D, YYYY'),
        events,
      }));
  }, [data?.events]);

  if (user?.role !== 'author') {
    return <Alert type="error" message="Access denied" showIcon />;
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert
        type="error"
        message="Visitor not found"
        description="Could not load data for this visitor."
        showIcon
        style={{ margin: '24px 0' }}
      />
    );
  }

  const { visitor, stats } = data;
  const displayName = visitor.label || visitor.visitorHash.slice(0, 12) + '...';

  return (
    <div style={{ maxWidth: '1000px' }}>
      {/* Back link */}
      <Link
        to="/reviewer/analytics"
        style={{ color: '#888', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '16px' }}
      >
        <ArrowLeftOutlined /> Back to Analytics
      </Link>

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
          <Title level={3} style={{ margin: 0, color: '#f5f5f5' }}>
            {displayName}
          </Title>
          <LabelEditor
            visitorHash={visitor.visitorHash}
            currentLabel={visitor.label}
            onLabelChange={() => queryClient.invalidateQueries({ queryKey: ['visitorDetail', visitorHash] })}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {visitor.ipAddress && (
            <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: '12px' }}>
              {visitor.ipAddress}
            </Text>
          )}
          {(visitor.city || visitor.country) && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <GlobalOutlined style={{ color: '#666', fontSize: '12px' }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {[visitor.city, visitor.country].filter(Boolean).join(', ')}
              </Text>
            </span>
          )}
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <DesktopOutlined style={{ color: '#666', fontSize: '12px' }} />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {visitor.device} / {visitor.browser}
            </Text>
          </span>
        </div>
      </div>

      {/* Stats cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small" style={{ background: '#1a1a1a', borderColor: '#333' }}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: '12px' }}>Total Views</Text>}
              value={stats.totalViews}
              prefix={<EyeOutlined style={{ color: '#d4af37' }} />}
              valueStyle={{ color: '#f5f5f5', fontSize: '20px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small" style={{ background: '#1a1a1a', borderColor: '#333' }}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: '12px' }}>Unique Pages</Text>}
              value={stats.uniquePages}
              prefix={<FileTextOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#f5f5f5', fontSize: '20px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small" style={{ background: '#1a1a1a', borderColor: '#333' }}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: '12px' }}>Total Time</Text>}
              value={formatDuration(stats.totalDuration)}
              prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#f5f5f5', fontSize: '20px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={6}>
          <Card size="small" style={{ background: '#1a1a1a', borderColor: '#333' }}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: '12px' }}>First Seen</Text>}
              value={dayjs(stats.firstSeen).format('MMM D, YYYY')}
              prefix={<CalendarOutlined style={{ color: '#13c2c2' }} />}
              valueStyle={{ color: '#f5f5f5', fontSize: '14px' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={6}>
          <Card size="small" style={{ background: '#1a1a1a', borderColor: '#333' }}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: '12px' }}>Last Seen</Text>}
              value={dayjs(stats.lastSeen).fromNow()}
              prefix={<CalendarOutlined style={{ color: '#eb2f96' }} />}
              valueStyle={{ color: '#f5f5f5', fontSize: '14px' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Timeline */}
      <Card
        title={<Text style={{ color: '#d9d9d9' }}>Browsing Timeline</Text>}
        style={{ background: '#1a1a1a', borderColor: '#333' }}
        styles={{ body: { padding: '16px 24px' } }}
      >
        {groupedEvents.map((group) => (
          <div key={group.date} style={{ marginBottom: '24px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
                paddingBottom: '8px',
                borderBottom: '1px solid #222222',
              }}
            >
              <Text strong style={{ color: '#d9d9d9', fontSize: '13px' }}>
                {group.dateLabel}
              </Text>
              <Tag style={{ background: '#242424', borderColor: '#333', color: '#888', fontSize: '11px' }}>
                {group.events.length} view{group.events.length !== 1 ? 's' : ''}
              </Tag>
            </div>
            <Timeline
              items={group.events.map((event) => ({
                color: event.essaySlug ? '#d4af37' : '#555',
                children: (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
                    <Text style={{ color: '#888', fontSize: '12px', fontFamily: 'monospace', minWidth: '80px' }}>
                      {dayjs(event.viewedAt).format('h:mm:ss A')}
                    </Text>
                    <Text style={{ color: event.essaySlug ? '#d9d9d9' : '#999', fontSize: '13px' }}>
                      {formatPageName(event)}
                    </Text>
                    {event.duration != null && event.duration > 0 && (
                      <Tag
                        style={{
                          background: event.duration >= 300 ? 'rgba(82, 196, 26, 0.15)' : 'rgba(124, 143, 166, 0.1)',
                          borderColor: event.duration >= 300 ? 'rgba(82, 196, 26, 0.3)' : 'rgba(124, 143, 166, 0.2)',
                          color: event.duration >= 300 ? '#52c41a' : '#7c8fa6',
                          fontSize: '11px',
                        }}
                      >
                        {formatDuration(event.duration)}
                      </Tag>
                    )}
                    {event.scrollDepth != null && (
                      <Tooltip title="Max scroll depth">
                        <Tag style={{ background: 'rgba(250, 173, 20, 0.15)', borderColor: 'rgba(250, 173, 20, 0.3)', color: '#faad14', fontSize: '11px' }}>
                          {event.scrollDepth}% scrolled
                        </Tag>
                      </Tooltip>
                    )}
                    {event.videoProgress != null && (
                      <Tooltip title="Video progress">
                        <Tag style={{ background: 'rgba(114, 46, 209, 0.15)', borderColor: 'rgba(114, 46, 209, 0.3)', color: '#b37feb', fontSize: '11px' }}>
                          {event.videoProgress}% watched
                        </Tag>
                      </Tooltip>
                    )}
                    {event.referrer && (
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        via {event.referrer.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                      </Text>
                    )}
                  </div>
                ),
              }))}
            />
          </div>
        ))}
        {groupedEvents.length === 0 && (
          <Text type="secondary">No pageview events found.</Text>
        )}
      </Card>
    </div>
  );
}

export default VisitorDetailPage;
